import { LevelMap } from "../levels/LevelMap";
import {
  PLACEMENT_TYPE_CHIP,
  PLACEMENT_TYPE_HERO,
  LEVEL_THEMES,
  PLACEMENT_TYPE_SWITCH_WALL,
  MODE_EDIT,
  MODE_PLAY,
  OTHER_DEATH_TYPES,
} from "../helpers/consts";
import { Clock } from "./Clock";
import { DirectionControls } from "./DirectionControls";
import sounds, { SFX } from "./Sounds";
import { Collision } from "./Collision";
import { AnimatedTiles } from "./AnimatedTiles";
import { Inventory } from "./Inventory";
import { LevelEditor } from "./LevelEditor";
import placementFactory from "./PlacementFactory";
import { GameLoop } from "./GameLoop";
import { Camera } from "./Camera";

export class LevelState {
  constructor(levelId, onEmit) {
    this.id = LevelMap[levelId] ? levelId : "G1-1";
    this.onEmitCallback = onEmit;
    this.animatedTiles = new AnimatedTiles();
    this.editor = new LevelEditor(this);
    this.directionControls = new DirectionControls({
      editKey: () => {
        if (this.isLevelCompleted) {
          return;
        }
        this.setMode(MODE_EDIT);
      },
      playKey: () => {
        if (this.isLevelCompleted) {
          return;
        }
        this.setMode(MODE_PLAY);
      },
      restartKey: () => {
        if (this.isLevelCompleted) {
          return;
        }
        this.start();
      },
    });

    // Set the one-time story message
    const data = LevelMap[this.id];
    this.story = data.story ?? null;

    // Kick it off!
    this.start();
  }

  start() {
    this.mode = MODE_PLAY;
    this.initialData = LevelMap[this.id];
    this.title = this.initialData.title ?? "";
    this.theme = this.initialData.theme ?? LEVEL_THEMES.YELLOW;
    this.tilesWidth = this.initialData.tilesWidth ?? 10;
    this.tilesHeight = this.initialData.tilesHeight ?? 10;
    this.timeLimitSeconds = this.initialData.timeLimitSeconds ?? 90;
    this.musicTrack = this.initialData.musicTrack ?? "MUSIC_WORLD1"; //TODO - const?
    this.isEditableByUser = this.initialData.isEditableByUser ?? false;

    // Sets timer for this level
    this.clock = new Clock(this.timeLimitSeconds);
    this.inventory = new Inventory();
    this.heroDeath = null;
    this.isLevelCompleted = false;

    //Turn each data placement into a real instance
    this.placements = this.initialData.placements.map((data) => {
      return placementFactory.createPlacement(data, this);
    });
    this.placements.forEach((p) => {
      const spawn = p.spawnPlacementOnStart();
      if (spawn) {
        this.spawnLivePlacement(spawn);
      }
    });

    //Create camera
    this.camera = new Camera(this);

    //Start ticking!
    this.gameLoop?.stop();
    this.gameLoop = new GameLoop(() => {
      this.tick();
    });

    //Start music
    sounds.playMusic(this.musicTrack);
  }

  get heroRef() {
    return this.placements.find((p) => p.type === PLACEMENT_TYPE_HERO);
  }

  get isPlayingMode() {
    return this.mode === MODE_PLAY;
  }

  get isEditMode() {
    return this.mode === MODE_EDIT;
  }

  get areAllChipsCollected() {
    const nonCollectedChip = this.placements.find((p) => {
      return p.type === PLACEMENT_TYPE_CHIP && !p.hasBeenCollected;
    });
    return Boolean(!nonCollectedChip);
  }

  spawnLivePlacement(placementConfig) {
    const newInstance = placementFactory.createPlacement(placementConfig, this);
    newInstance.wasSpawnedAfterMapStart = true;
    this.placements.push(newInstance);
    return newInstance;
  }

  updateLivePlacement(placementReference, changes = {}) {
    Object.keys(changes).forEach((key) => {
      placementReference[key] = changes[key];
    });
  }

  removeLivePlacement(placementReference) {
    this.placements = this.placements.filter((p) => {
      return p.id !== placementReference.id;
    });
  }

  tick() {
    // Run either Edit process
    if (this.isEditMode) {
      this.editModeTick();
      return;
    }
    // or Play process
    this.playModeTick();
  }

  editModeTick() {
    //Keep the Camera in sync with Hero
    this.camera.tick();

    // Check for movement input (Hero can move in Edit mode)
    const heldDirection = this.directionControls.direction;
    if (heldDirection) {
      this.heroRef.controllerMoveRequested(heldDirection);
    }

    //Hero is allowed to update
    this.heroRef.tick();

    // Emit to update React state
    this.onEmitCallback(this.getState());
  }

  playModeTick() {
    //Keep the Camera in sync with Hero
    this.camera.tick();

    if (this.isPlayModePaused) {
      return;
    }

    // Check for movement input
    const heldDirection = this.directionControls.direction;
    if (heldDirection) {
      this.heroRef.controllerMoveRequested(heldDirection);
    }

    // Call 'tick' on any instances that use the game loop for counting frames
    this.placements.forEach((p) => {
      p.tick();
    });

    // Tick down the updating clock if a second has gone by
    this.clock.tick(() => {
      this.handleSecondPassed();
    });

    // Animated frames progress
    this.animatedTiles.tick();

    // Emit to update React state
    this.onEmitCallback(this.getState());
  }

  get isPlayModePaused() {
    return this.isLevelCompleted || this.heroDeath || this.story;
  }

  isSpaceFree(x, y, forBody) {
    if (this.isOutOfBounds(x, y)) {
      return false;
    }
    const collision = new Collision(forBody, this);
    collision.setPosition(x, y);
    return !collision.withSolidTile();
  }

  isOutOfBounds(x, y) {
    return (
      x === 0 ||
      y === 0 ||
      x >= this.tilesWidth + 1 ||
      y >= this.tilesHeight + 1
    );
  }

  switchWalls() {
    this.placements.forEach((p) => {
      if (p.type === PLACEMENT_TYPE_SWITCH_WALL) {
        p.toggleRaised();
      }
    });
  }

  collectTile(placement, collectingBody) {
    const sfx = placement.playSfxOnCollect();
    if (sfx) {
      sounds.playSfx(sfx);
    }

    //Add it to `inventory`
    const inventoryKey = placement.addsItemToInventoryOnCollide(collectingBody);
    this.inventory.add(inventoryKey);

    //Delete this placement from state
    const pickedUp = this.placements.find((p) => p.id === placement.id);
    pickedUp.hasBeenCollected = true;

    // Play SFX if portal is now open
    if (pickedUp.type === PLACEMENT_TYPE_CHIP && this.areAllChipsCollected) {
      sounds.playSfx(SFX.THUMP);
    }
  }

  removeFromInventory(key) {
    this.inventory.remove(key);
  }

  resetAllInventory() {
    this.inventory.clearAll();
    this.placements.forEach((p) => {
      if (p.canBeStolen()) {
        p.hasBeenCollected = false;
      }
    });
  }

  handleSecondPassed() {
    if (this.clock.secondsLeft <= 0) {
      this.setDeathType(OTHER_DEATH_TYPES.CLOCK);
    }
  }

  setDeathType(deathType) {
    this.heroDeath = {
      deathType,
    };
    sounds.playSfx(SFX.DEATH);
  }

  completeLevel() {
    this.isLevelCompleted = true;
    sounds.playSfx(SFX.LEVEL_COMPLETE);
  }

  setMode(newValue) {
    if (newValue === MODE_EDIT && !this.isEditableByUser) {
      return;
    }
    this.mode = newValue;
  }

  closeStory() {
    this.story = null;
  }

  getState() {
    return {
      title: this.title,
      story: this.story,
      theme: this.theme,
      inventory: this.inventory,
      areAllChipsCollected: this.areAllChipsCollected,
      isLevelCompleted: this.isLevelCompleted,
      heroDeath: this.heroDeath,
      placements: this.placements,
      heroRef: this.heroRef,
      tilesWidth: this.tilesWidth,
      tilesHeight: this.tilesHeight,
      secondsLeft: this.clock.secondsLeft,
      musicTrack: this.musicTrack,
      cameraTransformX: this.camera.transformX,
      cameraTransformY: this.camera.transformY,
      isEditMode: this.isEditMode,
      isPlayingMode: this.isPlayingMode,
      isEditableByUser: this.isEditableByUser,

      //Reference to Classes
      editor: this.editor,
      directionControls: this.directionControls,

      //Exposed commands
      closeStory: this.closeStory.bind(this),
      setMode: this.setMode.bind(this),
      restart: () => {
        this.start();
      },
    };
  }

  // Cleans up anything that needs to happen when this instance is no longer used
  destroy() {
    this.directionControls.unbind();
    this.gameLoop.stop();
  }
}
